import type { CSSProperties, ReactNode } from 'react'

/**
 * UI-002 — shared page chrome.
 *
 * Every high-traffic screen hand-rolled its own topbar, and the audit found
 * them opening with several equal-weight bands: title, greeting, status,
 * filters and actions all competing before any analysis content. This
 * component fixes the HIERARCHY, not just the markup:
 *
 *   band 1  title + meta + primary actions   — what this page is, what to do
 *   band 2  `secondary` (filters, scopes)    — visually subordinate, quieter
 *
 * `variant` exists so migrating a page does not silently restyle it:
 *   display  the Instrument Serif brand treatment the intelligence screens
 *            already use (Alert Feed, Price Intelligence)
 *   compact  the smaller sans title used by admin/commercial screens
 *            (Block Management, Billing)
 *
 * Live review (2026-08-17) found the control area dominating before analysis
 * content began at narrow widths, so both bands wrap rather than overflow and
 * the vertical padding tightens under `dense`.
 */
type PageHeaderVariant = 'display' | 'compact'

interface PageHeaderProps {
  title: ReactNode
  /** Quiet mono line under the title — counts, scope, freshness. */
  subtitle?: ReactNode
  /** Primary actions, right-aligned in the title band. */
  actions?: ReactNode
  /** Subordinate band: filters, scope pickers, segment controls. */
  secondary?: ReactNode
  onBack?: () => void
  backLabel?: ReactNode
  variant?: PageHeaderVariant
  /** Tightens vertical rhythm where the page is already control-heavy. */
  dense?: boolean
  paddingX?: number
  /** Explicit vertical padding. `dense` is the preferred lever; this exists
      for screens that were tuned by hand before the component existed. */
  paddingY?: number
  titleTestId?: string
}

export function PageHeader({
  title,
  subtitle,
  actions,
  secondary,
  onBack,
  backLabel = 'Back',
  variant = 'display',
  dense = false,
  paddingX = 24,
  paddingY,
  titleTestId,
}: PageHeaderProps) {
  const display = variant === 'display'
  const padY = paddingY ?? (dense ? 9 : 12)

  const shellStyle: CSSProperties = {
    borderBottom: '1px solid var(--border)',
    flexShrink: 0,
    background: 'var(--surface)',
    // The header owns filter dropdowns on several screens; without a stacking
    // context above the scroll body those popovers paint underneath the first
    // rows of the list.
    position: 'relative',
    zIndex: 30,
  }

  const backStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    fontFamily: 'var(--mono)',
    fontSize: 10,
    color: 'var(--accent)',
    cursor: 'pointer',
    flexShrink: 0,
    background: 'transparent',
    border: 'none',
    padding: 0,
  }

  const titleStyle: CSSProperties = display
    ? {
        fontFamily: 'var(--display)',
        // `dense` used to tighten padding only, which left a full-size title
        // sitting in a deliberately compressed band. It steps the title down
        // too, on every page that opts in.
        fontSize: dense ? 19 : 22,
        fontWeight: 400,
        color: 'var(--text-1)',
        letterSpacing: '-0.015em',
        lineHeight: 1.1,
      }
    : {
        fontSize: dense ? 15 : 17,
        fontWeight: 700,
        color: 'var(--text-1)',
        letterSpacing: '-0.02em',
      }

  const subtitleStyle: CSSProperties = {
    fontFamily: 'var(--mono)',
    fontSize: 10,
    color: 'var(--text-3)',
    marginTop: display ? 3 : 2,
  }

  return (
    // `data-page-header` is the stable measurement hook: paired with
    // `data-page-controls` on FilterBar it lets one spec measure the top-of-page
    // band stack on any screen without per-page selectors.
    <div data-page-header="" style={shellStyle}>
      {/* Band 1 — identity and primary actions. `l-wrap` so a long action
          cluster drops to its own line instead of sliding off the edge. */}
      <div className="l-wrap" style={{ gap: 14, padding: `${padY}px ${paddingX}px` }}>
        {onBack ? (
          <button type="button" style={backStyle} onClick={onBack}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ width: 12, height: 12 }}>
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {backLabel}
          </button>
        ) : null}
        <div style={{ minWidth: 0 }}>
          <div data-testid={titleTestId} style={titleStyle}>{title}</div>
          {subtitle ? <div style={subtitleStyle}>{subtitle}</div> : null}
        </div>
        {actions ? (
          <div className="l-wrap" style={{ marginLeft: 'auto', gap: 8, justifyContent: 'flex-end' }}>
            {actions}
          </div>
        ) : null}
      </div>

      {/* Band 2 — controls. Subordinate by surface and by rhythm: it sits on
          --surface-2 and is separated by a rule, so the eye reaches the page's
          primary task before it reaches filter detail. */}
      {secondary ? (
        <div
          className="l-wrap"
          data-page-controls=""
          style={{
            gap: 6,
            padding: `8px ${paddingX}px`,
            borderTop: '1px solid var(--border)',
            background: 'var(--surface-2)',
          }}
        >
          {secondary}
        </div>
      ) : null}
    </div>
  )
}
